import React from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Tooltip, Typography } from '@mui/material';
import { InfoTip } from '../shared/InfoTip';
import type { RemindersByVaccine, VaccineReminderCounts } from '../../services/dashboardService';

interface RemindersByVaccineCardProps {
  data: RemindersByVaccine | null;
  loading: boolean;
  error: string | null;
}

const INFO =
  'Worked out from the vaccines each person already had, using the MOH 2025 schedule. ' +
  'DHIS2 does not record which vaccine a scheduled visit is for.';

// Sent and pending share one hue, so the stack reads as one total split in two.
const SEGMENTS = [
  { key: 'sent', label: 'Sent', color: 'primary.main' },
  { key: 'pending', label: 'Pending', color: 'primary.light' },
] as const;

interface DoseRowProps {
  label: string;
  counts: VaccineReminderCounts;
  /** Largest row total on the card, so every bar shares one scale. */
  max: number;
  muted?: boolean;
}

const DoseRow: React.FC<DoseRowProps> = ({ label, counts, max, muted }) => {
  const summary = `${label}: ${counts.sent.toLocaleString()} sent, ${counts.pending.toLocaleString()} pending`;
  return (
    <Tooltip title={summary} placement="top">
      {/* tabIndex lets keyboard and touch users open the tooltip. */}
      <Box
        tabIndex={0}
        aria-label={summary}
        sx={{ display: 'grid', gridTemplateColumns: '96px 1fr auto', alignItems: 'center', columnGap: 1.5 }}
      >
        <Typography variant="body2" fontWeight={500} noWrap>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: '2px', height: 8, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
          {SEGMENTS.map((s) => (
            <Box
              key={s.key}
              sx={{
                width: `${max > 0 ? (counts[s.key] / max) * 100 : 0}%`,
                bgcolor: muted ? 'text.disabled' : s.color,
                opacity: muted && s.key === 'pending' ? 0.5 : 1,
                transition: 'width 0.5s ease',
              }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 150, textAlign: 'right' }}>
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {counts.sent.toLocaleString()}
          </Box>{' '}
          sent · {counts.pending.toLocaleString()} pending
        </Typography>
      </Box>
    </Tooltip>
  );
};

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export const RemindersByVaccineCard: React.FC<RemindersByVaccineCardProps> = ({ data, loading, error }) => {
  const totals = data ? [data.unknown, ...data.child, ...data.td].map((r) => r.sent + r.pending) : [];
  const max = Math.max(0, ...totals);

  return (
    <Card variant="outlined" elevation={0} sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Reminders by Vaccine
            <InfoTip label="Reminders by Vaccine" text={INFO} />
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {SEGMENTS.map((s) => (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : loading || !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Group title="Child vaccines">
              {data.child.map((r) => <DoseRow key={r.dose} label={r.label} counts={r} max={max} />)}
            </Group>
            <Group title="Td (women)">
              {data.td.map((r) => <DoseRow key={r.dose} label={r.label} counts={r} max={max} />)}
            </Group>
            <Group title="Not known">
              <DoseRow label="Unknown" counts={data.unknown} max={max} muted />
            </Group>
            <Typography variant="caption" color="text.secondary">
              One SMS can cover several vaccines, so these figures add up to more than SMS Sent.
              Unknown means we could not work out the vaccines for that visit.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
